import { Inject, Injectable } from '@angular/core'

import { Actions, createEffect, ofType } from '@ngrx/effects'
import { Observable, of } from 'rxjs'
import { catchError, map, switchMap, withLatestFrom } from 'rxjs/operators'

import { QuarterlyDashboardApiService } from '~/core/api'
import {
  IQuarterlyDashboardMktCap,
  IQuarterlyDashboardQuarter,
  IQuarterlyDashboardSector,
  IQuarterlyDashboardSectorTop500,
} from '~/core/models'

import * as QUARTERLY_DASHBOARD_ACTIONS from './quarterly-dashboard.actions'
import { QUARTERLY_DASHBOARD_MINIMUM_COMPANY_COUNT } from './quarterly-dashboard.constant'
import { IQuarterlyDashboardFacade, QUARTERLY_DASHBOARD_FACADE } from './quarterly-dashboard.facade'

@Injectable()
export class QuarterlyDashboardEffects {
  constructor(
    private actions$: Actions,
    private quarterlyDashboardApiService: QuarterlyDashboardApiService,
    @Inject(QUARTERLY_DASHBOARD_FACADE) private quarterlyDashboardFacade: IQuarterlyDashboardFacade
  ) {}

  // quarter
  getQuarterlyDashboardQuarterData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(QUARTERLY_DASHBOARD_ACTIONS.getQuarterlyDashboardQuarterData),
      withLatestFrom(this.quarterlyDashboardFacade.quarter$),
      switchMap(([, quarter]) =>
        // Don't reload data if exist for the same quarter data
        (quarter.initialized
          ? of(quarter.data)
          : this.quarterlyDashboardApiService.getQuarterlyDashboardQuarterList()
        ).pipe(
          switchMap(data => [
            QUARTERLY_DASHBOARD_ACTIONS.setQuarterlyDashboardQuarterSuccess({
              data,
              effective: this.findEffectiveQuarter(data),
            }),
            QUARTERLY_DASHBOARD_ACTIONS.setQuarterlyDashboardQuarterSelected({
              selected: this.findSelectedQuarter(data, quarter.selected),
            }),
          ])
        )
      )
    )
  )

  setQuarterlyDashboardQuarterSelected$ = createEffect(() =>
    this.actions$.pipe(
      ofType(QUARTERLY_DASHBOARD_ACTIONS.setQuarterlyDashboardQuarterSelected),
      switchMap(() => [
        QUARTERLY_DASHBOARD_ACTIONS.getQuarterlyDashboardMktCapData(),
        QUARTERLY_DASHBOARD_ACTIONS.getQuarterlyDashboardSectorData(),
        QUARTERLY_DASHBOARD_ACTIONS.getQuarterlyDashboardSectorTop500Data(),
      ])
    )
  )

  // market cap
  getQuarterlyDashboardMktCapData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(QUARTERLY_DASHBOARD_ACTIONS.getQuarterlyDashboardMktCapData),
      withLatestFrom(this.quarterlyDashboardFacade.quarterSelected$, this.quarterlyDashboardFacade.mktCap$),
      switchMap(([, quarterSelected, mktCap]) => {
        let mktCapData$: Observable<IQuarterlyDashboardMktCap[]>
        if (!quarterSelected && mktCap.data.length === 0) {
          mktCapData$ = of(mktCap.data)
        } else if (!quarterSelected) {
          mktCapData$ = of([])
        } else if (mktCap.initialized && mktCap.quarter === quarterSelected) {
          // Don't reload data if exist for the same quarter
          mktCapData$ = of(mktCap.data)
        } else {
          mktCapData$ = this.quarterlyDashboardApiService
            .getQuarterlyDashboardMktCap(quarterSelected.year, quarterSelected.quarter, mktCap.sortBy, mktCap.order)
            .pipe(map(response => response.data))
        }

        return mktCapData$.pipe(
          switchMap(data => [
            QUARTERLY_DASHBOARD_ACTIONS.setQuarterlyDashboardMktCapSuccess({
              data,
              quarter: quarterSelected,
            }),
          ]),
          catchError(error => [QUARTERLY_DASHBOARD_ACTIONS.setQuarterlyDashboardMktCapError({ error })])
        )
      })
    )
  )

  setQuarterlyDashboardMktCapSort$ = createEffect(() =>
    this.actions$.pipe(
      ofType(QUARTERLY_DASHBOARD_ACTIONS.setQuarterlyDashboardMktCapSort),
      switchMap(() => [QUARTERLY_DASHBOARD_ACTIONS.getQuarterlyDashboardMktCapData()])
    )
  )

  // sector
  getQuarterlyDashboardSectorData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(QUARTERLY_DASHBOARD_ACTIONS.getQuarterlyDashboardSectorData),
      withLatestFrom(this.quarterlyDashboardFacade.quarterSelected$, this.quarterlyDashboardFacade.sector$),
      switchMap(([, quarterSelected, sector]) => {
        let sectorData$: Observable<IQuarterlyDashboardSector[]>
        if (!quarterSelected && sector.data.length === 0) {
          sectorData$ = of(sector.data)
        } else if (!quarterSelected) {
          sectorData$ = of([])
        } else if (sector.initialized && sector.quarter === quarterSelected) {
          // Don't reload data if exist for the same quarter
          sectorData$ = of(sector.data)
        } else {
          sectorData$ = this.quarterlyDashboardApiService
            .getQuarterlyDashboardSector(quarterSelected.year, quarterSelected.quarter, sector.sortBy, sector.order)
            .pipe(map(response => response.data))
        }

        return sectorData$.pipe(
          switchMap(data => [
            QUARTERLY_DASHBOARD_ACTIONS.setQuarterlyDashboardSectorSuccess({
              data,
              quarter: quarterSelected,
            }),
          ]),
          catchError(error => [QUARTERLY_DASHBOARD_ACTIONS.setQuarterlyDashboardSectorError({ error })])
        )
      })
    )
  )

  setQuarterlyDashboardSectorSort$ = createEffect(() =>
    this.actions$.pipe(
      ofType(QUARTERLY_DASHBOARD_ACTIONS.setQuarterlyDashboardSectorSort),
      switchMap(() => [QUARTERLY_DASHBOARD_ACTIONS.getQuarterlyDashboardSectorData()])
    )
  )

  // sectorTop500
  getQuarterlyDashboardSectorTop500Data$ = createEffect(() =>
    this.actions$.pipe(
      ofType(QUARTERLY_DASHBOARD_ACTIONS.getQuarterlyDashboardSectorTop500Data),
      withLatestFrom(this.quarterlyDashboardFacade.quarterSelected$, this.quarterlyDashboardFacade.sectorTop500$),
      switchMap(([, quarterSelected, sectorTop500]) => {
        let sectorTop500Data$: Observable<IQuarterlyDashboardSectorTop500[]>
        if (!quarterSelected && sectorTop500.data.length === 0) {
          sectorTop500Data$ = of(sectorTop500.data)
        } else if (!quarterSelected) {
          sectorTop500Data$ = of([])
        } else if (sectorTop500.initialized && sectorTop500.quarter === quarterSelected) {
          // Don't reload data if exist for the same quarter
          sectorTop500Data$ = of(sectorTop500.data)
        } else {
          sectorTop500Data$ = this.quarterlyDashboardApiService
            .getQuarterlyDashboardSectorTop500(
              quarterSelected.year,
              quarterSelected.quarter,
              sectorTop500.sortBy,
              sectorTop500.order
            )
            .pipe(map(response => response.data))
        }

        return sectorTop500Data$.pipe(
          switchMap(data => [
            QUARTERLY_DASHBOARD_ACTIONS.setQuarterlyDashboardSectorTop500Success({
              data,
              quarter: quarterSelected,
            }),
          ]),
          catchError(error => [QUARTERLY_DASHBOARD_ACTIONS.setQuarterlyDashboardSectorTop500Error({ error })])
        )
      })
    )
  )

  setQuarterlyDashboardSectorTop500Sort$ = createEffect(() =>
    this.actions$.pipe(
      ofType(QUARTERLY_DASHBOARD_ACTIONS.setQuarterlyDashboardSectorTop500Sort),
      switchMap(() => [QUARTERLY_DASHBOARD_ACTIONS.getQuarterlyDashboardSectorTop500Data()])
    )
  )

  // miscellaneous
  private findEffectiveQuarter(data: IQuarterlyDashboardQuarter[]): IQuarterlyDashboardQuarter | undefined {
    if (!data.length) {
      return undefined
    }
    for (let index = data.length - 1; index >= 0; index--) {
      const item = data[index]
      if (item.reportedCount >= QUARTERLY_DASHBOARD_MINIMUM_COMPANY_COUNT) {
        return item
      }
    }
    return undefined
  }

  private findSelectedQuarter(
    data: IQuarterlyDashboardQuarter[],
    previousSelected: IQuarterlyDashboardQuarter | undefined
  ): IQuarterlyDashboardQuarter | undefined {
    if (!data.length) {
      return undefined
    }
    if (previousSelected && data.includes(previousSelected)) {
      return previousSelected
    }
    if (previousSelected) {
      const newSelected = data.find(
        item => item.year === previousSelected.year && item.quarter === previousSelected.quarter
      )
      if (newSelected) {
        return newSelected
      }
    }
    return this.findEffectiveQuarter(data)
  }
}
